import { motion, useScroll, useTransform } from "framer-motion";
import { BookOpen } from "lucide-react";
import { BOOK } from "../lib/content";
import { BookCover } from "./BookCover";
import { scrollToSection } from "./Navbar";

const RevealLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onOpenReader }) => {
  const { scrollYProgress } = useScroll();
  const bookY = useTransform(scrollYProgress, [0, 0.25], [0, 130]);
  const fade = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,160,89,0.08),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="h-px w-12 bg-[#C5A059]" />
            <span className="label-eyebrow" data-testid="hero-status-badge">{BOOK.status}</span>
          </motion.div>
          <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]" data-testid="hero-title">
            <RevealLine delay={0.5}>Juega</RevealLine>
            <RevealLine delay={0.65} className="text-gold">Tu Partido</RevealLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-6 font-serif-book italic text-xl sm:text-2xl text-[#E2C08D]"
            data-testid="hero-tagline"
          >
            {BOOK.tagline}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#A19F9A] font-light"
            data-testid="hero-synopsis"
          >
            {BOOK.heroSynopsis} <span className="text-[#F2EFE9]">{BOOK.heroSub}</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-notify-button"
              onClick={() => scrollToSection("#newsletter")}
              className="px-8 py-4 bg-[#C5A059] text-[#0A0A0E] text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#DFB86B] transition-colors duration-300"
            >
              Avisarme del lanzamiento
            </button>
            <button
              data-testid="hero-sample-chapter-button"
              onClick={onOpenReader}
              className="group flex items-center gap-3 px-8 py-4 border border-white/15 text-xs uppercase tracking-[0.25em] text-[#F2EFE9] hover:border-[#C5A059]/60 hover:text-[#E2C08D] transition-colors duration-300"
            >
              <BookOpen size={15} className="transition-transform duration-300 group-hover:-rotate-6" />
              Leer la introducción
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.25em] text-[#6E6C67]"
          >
            <span>{BOOK.genre}</span>
            <span className="w-1 h-1 rotate-45 bg-[#C5A059]/60" />
            <span>{BOOK.isbn}</span>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: bookY }} className="lg:col-span-5">
          <BookCover />
        </motion.div>
      </div>
    </section>
  );
};
